import { useTranslation } from "react-i18next";
import { ArrowUp, Mic, Paperclip, Square, X } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import toast from "react-hot-toast";

import { AttachmentChips } from "@/components/ai-assistant/attachment-chips";
import { AI_PROMPT_MAX_LENGTH } from "@/components/ai-assistant/context";
import { AudioWaveform } from "@/components/ui/audio-waveform";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  MAYA_ACCEPT_ATTRIBUTE,
  type IMayaAttachment,
} from "@/hooks/useMayaFileUploads";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { cn } from "@/utils/cn";

import { COMPOSER_MAX_HEIGHT_PX } from "../constants";
import { useMayaSpace } from "../context/use-maya-space";

export interface ComposerHandle {
  focus: () => void;
  setValue: (value: string) => void;
}

/** Files attached to the turn being written (see useMayaFileUploads). */
export interface ComposerUploads {
  attachments: IMayaAttachment[];
  addFiles: (files: FileList | File[]) => void;
  removeAttachment: (id: string) => void;
  retryAttachment: (id: string) => void;
  hasUploading: boolean;
  /** Uploaded and ready to send. */
  readyCount: number;
}

interface ComposerProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: (text: string) => void;
  uploads: ComposerUploads;
  /** Next to the mic — e.g. the advisor picker. */
  actionSlot?: ReactNode;
  placeholder?: string;
  /** Temporary chat: a dashed ring, so it's clear nothing is being saved. */
  temporary?: boolean;
  /** A response is streaming. The field stays enabled (type-ahead); the send
   *  button becomes a stop button. */
  isStreaming?: boolean;
  onStop?: () => void;
  /** Hold sending (typing still works) — e.g. while a saved chat opens. */
  sendBlocked?: boolean;
}

const autoResize = (el: HTMLTextAreaElement) => {
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
};

export const Composer = forwardRef<ComposerHandle, ComposerProps>(
  (
    {
      value,
      onChange,
      onSubmit,
      uploads,
      actionSlot,
      placeholder,
      temporary = false,
      isStreaming = false,
      onStop,
      sendBlocked,
    },
    ref,
  ) => {
    const { t } = useTranslation(["common", "my-space"]);
    const inputId = useId();
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const recorder = useVoiceRecorder();
    const [isTranscribing, setIsTranscribing] = useState(false);
    const space = useMayaSpace();
    const [speechToText] = space.useSpeechToText();

    const hasText = value.trim().length > 0;
    // Text or uploaded files; never while a file is still uploading.
    const canSend =
      (hasText || uploads.readyCount > 0) &&
      !isTranscribing &&
      !sendBlocked &&
      !uploads.hasUploading;

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      setValue: (next: string) => {
        onChange(next);
        requestAnimationFrame(() => {
          if (textareaRef.current) {
            autoResize(textareaRef.current);
            textareaRef.current.focus();
          }
        });
      },
    }));

    useEffect(() => {
      if (textareaRef.current) autoResize(textareaRef.current);
    }, [value]);

    const finalizeRecording = async () => {
      let text = "";
      try {
        const audioBlob = await recorder.stop();
        setIsTranscribing(true);
        const form = new FormData();
        form.append("audio", audioBlob, "recording.webm");
        const res = await speechToText(form).unwrap();
        text = res.data?.output ?? "";
      } catch (err) {
        console.error(err);
        toast.error(t("my-space:toasts.could_not_transcribe_audio"));
        setIsTranscribing(false);
        return;
      }
      setIsTranscribing(false);
      const merged = value ? `${value} ${text}`.trim() : text.trim();
      if (!merged) return;
      onChange(merged);
      requestAnimationFrame(() => textareaRef.current?.focus());
    };

    const handleMicClick = async () => {
      if (isStreaming || isTranscribing || recorder.isRecording) return;
      try {
        await recorder.start({
          onTimeout: () =>
            toast.error(
              t("my-space:toasts.recording_stopped_2_minute_limit_reached"),
            ),
        });
      } catch {
        const err = recorder.error;
        if (err === "permission-denied")
          toast.error(t("my-space:toasts.microphone_permission_denied"));
        else if (err === "no-device")
          toast.error(t("my-space:toasts.no_microphone_found"));
        else if (err === "unsupported")
          toast.error(
            t("my-space:toasts.voice_input_not_supported_in_this_browser"),
          );
        else toast.error(t("my-space:toasts.could_not_start_recording"));
      }
    };

    const handleCancelRecording = () => {
      recorder.cancel();
    };

    const dispatchSend = () => {
      // Enter is ignored while streaming — the field stays open for type-ahead,
      // but the message only sends once the current response finishes.
      if (isStreaming) return;
      if (uploads.hasUploading) {
        toast.error(
          t("my-space:toasts.wait_for_attachments_to_finish_uploading"),
        );
        return;
      }
      // maxLength stops typing/pasting past the cap; a voice merge can't.
      if (value.trim().length > AI_PROMPT_MAX_LENGTH) {
        toast.error(
          t("my-space:toasts.message_is_too_long_max_characters", {
            count: AI_PROMPT_MAX_LENGTH,
          }),
        );
        return;
      }
      if (!canSend) return;
      onSubmit(value.trim());
    };

    // Files pasted into the field (e.g. a screenshot) attach like picked ones;
    // pasted text falls through to the default.
    const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length === 0) return;
      // Cells copied from a spreadsheet or a document arrive as text plus a
      // picture of them — the text is what was meant, so paste that.
      const text = e.clipboardData.getData("text/plain");
      if (text.trim() && files.every((f) => f.type.startsWith("image/")))
        return;
      e.preventDefault();
      uploads.addFiles(files);
    };

    const handleSubmit = async (e: FormEvent) => {
      e.preventDefault();
      if (recorder.isRecording) {
        await finalizeRecording();
        return;
      }
      dispatchSend();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Not while an IME is composing — Enter confirms the candidate then.
      if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        if (recorder.isRecording) {
          finalizeRecording();
          return;
        }
        dispatchSend();
      }
    };

    const sendDisabled = !recorder.isRecording && !canSend;
    const textareaDisabled = recorder.isRecording || isTranscribing;
    const showStop = isStreaming && !recorder.isRecording && !!onStop;

    return (
      <form onSubmit={handleSubmit} className="px-3 pt-1 pb-2">
        {/* Floating pill: a hairline at rest, the brand gradient + glow while
            focused (see .maya-composer). */}
        <div className={cn("maya-composer", temporary && "maya-composer-temp")}>
          <div className="flex flex-col rounded-[calc(var(--radius-lg)-1px)] bg-surface">
            {uploads.attachments.length > 0 && (
              <AttachmentChips
                attachments={uploads.attachments}
                onRemove={uploads.removeAttachment}
                onRetry={uploads.retryAttachment}
                size="sm"
                className="px-3 pt-2.5"
              />
            )}
            <div className="flex items-end gap-1 p-1.5 ps-1.5">
              <label htmlFor={inputId} className="sr-only">
                {t("common:chrome.message_maya")}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={MAYA_ACCEPT_ATTRIBUTE}
                onChange={(e) => {
                  if (e.target.files?.length) uploads.addFiles(e.target.files);
                  e.target.value = "";
                }}
                className="hidden"
                tabIndex={-1}
                aria-hidden="true"
              />
              {!recorder.isRecording && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isTranscribing}
                  aria-label={t("my-space:labels.attach_files")}
                  title={t("my-space:ui.attach_files_images_and_pdf")}
                  className="rounded-full text-muted-foreground hover:text-foreground"
                >
                  <Paperclip className="size-4" />
                </Button>
              )}
              {recorder.isRecording ? (
                <span
                  className="flex h-8 min-w-0 flex-1 items-center gap-1.5 ps-1.5 text-[11px] font-medium text-muted-foreground"
                  aria-live="polite"
                >
                  <span className="size-1.5 shrink-0 animate-mic-pulse rounded-full bg-danger" />
                  <AudioWaveform
                    stream={recorder.stream}
                    active={recorder.isRecording}
                    barCount={14}
                    className="h-4 shrink-0"
                  />
                  <span className="truncate">
                    {t("common:chrome.listening_tap_send_when_done")}
                  </span>
                </span>
              ) : (
                <textarea
                  ref={textareaRef}
                  id={inputId}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  maxLength={AI_PROMPT_MAX_LENGTH}
                  placeholder={
                    isTranscribing
                      ? t("common:chrome.transcribing")
                      : (placeholder ?? t("common:chrome.ask_maya_anything"))
                  }
                  autoComplete="off"
                  rows={1}
                  disabled={textareaDisabled}
                  className="flex-1 resize-none overflow-hidden bg-transparent py-1.5 text-base leading-snug text-foreground placeholder:text-muted-foreground focus:outline-hidden disabled:opacity-60 sm:text-xs"
                  style={{ maxHeight: COMPOSER_MAX_HEIGHT_PX }}
                />
              )}
              <div className="flex shrink-0 items-center gap-1">
                {!recorder.isRecording && actionSlot}
                {recorder.isRecording ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleCancelRecording}
                    aria-label={t("common:a11y.cancel_recording")}
                    className="rounded-full text-danger hover:bg-danger-muted"
                  >
                    <X className="size-3.5" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleMicClick}
                    disabled={isStreaming || isTranscribing}
                    aria-label={t("common:a11y.start_recording")}
                    className="rounded-full text-muted-foreground hover:text-foreground"
                  >
                    {isTranscribing ? (
                      <Spinner size="sm" className="text-muted-foreground" />
                    ) : (
                      <Mic className="size-4" />
                    )}
                  </Button>
                )}
                {showStop ? (
                  <Button
                    type="button"
                    size="icon-sm"
                    onClick={onStop}
                    className="rounded-full shadow-xs"
                    aria-label={t("common:a11y.stop_generating")}
                  >
                    <Square className="size-3 fill-current" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size="icon-sm"
                    disabled={sendDisabled}
                    className="rounded-full shadow-xs"
                    aria-label={
                      recorder.isRecording
                        ? t("studio:shared.finish_recording")
                        : t("common:chrome.send_message")
                    }
                  >
                    <ArrowUp className="size-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  },
);

Composer.displayName = "Composer";
